import { z } from 'zod';
import { REGION_VALUES } from '@/shared/utils/regions';
import { env } from '@/shared/lib/env';
import type { ApiAddressesGetItem, ApiAddressesPostBody } from '@/shared/types/api/addresses';

export const addressFormSchema = z.object({
  label: z
    .string()
    .trim()
    .min(2, 'addresses:errors.labelRequired')
    .max(60, 'addresses:errors.tooLong'),
  address_name: z
    .string()
    .trim()
    .min(2, 'addresses:errors.addressNameRequired')
    .max(120, 'addresses:errors.tooLong'),
  region: z.enum(REGION_VALUES, { required_error: 'addresses:errors.regionRequired' }),
  city: z
    .string()
    .trim()
    .min(2, 'addresses:errors.cityRequired')
    .max(80, 'addresses:errors.tooLong'),
  street: z
    .string()
    .trim()
    .min(2, 'addresses:errors.streetRequired')
    .max(120, 'addresses:errors.tooLong'),
  building: z
    .string()
    .trim()
    .min(1, 'addresses:errors.buildingRequired')
    .max(40, 'addresses:errors.tooLong'),
  apartment: z.string().trim().max(40, 'addresses:errors.tooLong').optional().or(z.literal('')),
  additional_info: z
    .string()
    .trim()
    .max(500, 'addresses:errors.tooLong')
    .optional()
    .or(z.literal('')),
  is_default: z.boolean(),
  // Recipient personal info — aligned with parcel-create section schemas
  first_name: z
    .string()
    .trim()
    .min(2, 'addresses:errors.firstNameRequired')
    .max(60, 'addresses:errors.tooLong'),
  last_name: z
    .string()
    .trim()
    .min(2, 'addresses:errors.lastNameRequired')
    .max(60, 'addresses:errors.tooLong'),
  middle_name: z
    .string()
    .trim()
    .min(2, 'addresses:errors.middleNameRequired')
    .max(60, 'addresses:errors.tooLong'),
  phone_number: z
    .string()
    .trim()
    .min(7, 'addresses:errors.phoneTooShort')
    .max(20, 'addresses:errors.tooLong')
    .regex(/^\+?[0-9\s()-]+$/u, 'addresses:errors.phoneInvalid'),
  passport_series: z
    .string()
    .min(1, 'addresses:errors.passportRequired')
    .regex(/^[A-Z]{2}[0-9]{7}$/, 'addresses:errors.passportInvalid'),
  pinfl: z
    .string()
    .trim()
    .length(env.pinflLength, 'addresses:errors.pinflLength')
    .regex(/^\d+$/u, 'addresses:errors.pinflDigitsOnly'),
});

export type AddressFormValues = z.infer<typeof addressFormSchema>;

export const emptyAddressFormValues: AddressFormValues = {
  label: '',
  address_name: '',
  region: 'TASHKENT_CITY',
  city: '',
  street: '',
  building: '',
  apartment: '',
  additional_info: '',
  is_default: false,
  first_name: '',
  last_name: '',
  middle_name: '',
  phone_number: '',
  passport_series: '',
  pinfl: '',
};

export const mapAddressToFormValues = (item: ApiAddressesGetItem): AddressFormValues => ({
  label: item.label,
  address_name: item.address_name,
  region: item.region,
  city: item.city,
  street: item.street,
  building: item.building,
  apartment: item.apartment ?? '',
  additional_info: item.additional_info ?? '',
  is_default: item.is_default,
  first_name: item.first_name ?? '',
  last_name: item.last_name ?? '',
  middle_name: item.middle_name ?? '',
  phone_number: item.phone_number ?? '',
  passport_series: item.passport_series ?? '',
  pinfl: item.pinfl ?? '',
});

export const mapFormValuesToPayload = (values: AddressFormValues): ApiAddressesPostBody => ({
  label: values.label,
  address_name: values.address_name,
  region: values.region,
  city: values.city,
  street: values.street,
  building: values.building,
  apartment: values.apartment ?? '',
  additional_info: values.additional_info ?? '',
  is_default: values.is_default,
  first_name: values.first_name || undefined,
  last_name: values.last_name || undefined,
  middle_name: values.middle_name ?? '',
  phone_number: values.phone_number || undefined,
  passport_series: values.passport_series || undefined,
  pinfl: values.pinfl || undefined,
});
